import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { parentApi, apiError } from "../../lib/api";
import { Card, Button, Input, Modal, Coins, Pill } from "../../components/ui";
import Avatar from "../../components/Avatar";
import IconTile from "../../components/IconTile";
import ConfirmDialog from "../../components/ConfirmDialog";
import TemplatePicker from "../../components/TemplatePicker";
import Icon from "../../components/Icon";
import { useAuth } from "../../context/AuthContext";
import { colorFor } from "../../lib/theme";

const RECURRENCE = [
  { key: "once", label: "One time" },
  { key: "daily", label: "Every day" },
  { key: "weekly", label: "Every week" },
];
const STATUS_LABEL = { todo: "To do", pending: "Waiting for you", approved: "Approved" };
const CHORE_ICONS = ["check", "bed", "utensils", "trash-2", "book", "dog", "cat", "flower", "sprout",
  "brush", "shirt", "bath", "smile", "car", "leaf", "droplet", "bone", "backpack", "music", "heart",
  "gamepad-2", "bike", "carrot", "cookie", "piano", "palette", "sandwich", "snowflake", "toy-brick", "star"];

export default function MissionStudio() {
  const { showEditControls, demoGuard } = useAuth();
  const [chores, setChores] = useState([]);
  const [children, setChildren] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("templates");
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({ title: "", points: 5, child_id: "", recurrence: "once", icon: "check" });

  const load = useCallback(async () => {
    try {
      const [c, k, t] = await Promise.all([
        parentApi.get("/chores"), parentApi.get("/children"), parentApi.get("/chore-templates"),
      ]);
      setChores(c.data.chores); setChildren(k.data.children); setTemplates(t.data.templates);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const kidName = (id) => children.find((k) => k.id === id)?.name || "Unassigned";
  const kidColor = (id) => children.find((k) => k.id === id)?.color || "#94A3B8";
  const filtered = filter === "all" ? chores : chores.filter((c) => c.status === filter);

  const openNew = () => { setEditing(null); setTab("templates"); setForm({ title: "", points: 5, child_id: children[0]?.id || "", recurrence: "once", icon: "check" }); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ title: c.title, points: c.points, child_id: c.child_id || "", recurrence: c.recurrence, icon: c.icon || "check" }); setTab("custom"); setModal(true); };
  const pickTemplate = (t) => { setForm((f) => ({ ...f, title: t.title, points: t.points, icon: t.icon })); setTab("custom"); };

  const save = async () => {
    if (demoGuard()) return;
    if (!form.title.trim()) { toast.error("Give the chore a name."); return; }
    try {
      if (editing) { await parentApi.put(`/chores/${editing.id}`, form); toast.success("Chore updated"); }
      else { await parentApi.post("/chores", form); toast.success("Chore assigned! 🌱"); }
      setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (demoGuard()) return false;
    try { await parentApi.delete(`/chores/${id}`); toast.success("Chore removed"); await load(); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const approve = async (id) => {
    if (demoGuard()) return;
    try { await parentApi.post(`/chores/${id}/approve`); toast.success("Points awarded! 🎉"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="grid place-items-center py-20"><div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="mission-studio">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl text-ink">Mission studio</h1>
          <p className="text-ink-soft font-medium">Assign chores and set their points.</p>
        </div>
        <Button onClick={openNew} data-testid="new-chore-btn" className={showEditControls ? "" : "hidden"}><Icon name="plus" width={18} height={18} /> New</Button>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        {["all", "todo", "pending", "approved"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-colors ${filter === s ? "bg-ink text-white" : "bg-white border border-slate-200 text-ink-soft"}`}>
            {s === "all" ? "All" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <Icon name="clipboard-list" className="mx-auto text-primary mb-3" width={36} height={36} />
          <p className="font-bold text-ink mb-4">No chores here yet.</p>
          {showEditControls && <Button onClick={openNew}>Assign a chore</Button>}
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <Card key={c.id} className="p-4 flex items-center gap-3 group hover:-translate-y-0.5 transition-transform duration-200" data-testid="chore-row">
              <IconTile name={c.icon || "check"} color={c.child_id ? kidColor(c.child_id) : colorFor(c.title)} size={52} floating />
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-ink truncate">{c.title}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Pill tone="slate"><Avatar avatar={children.find(k=>k.id===c.child_id)?.avatar} color={kidColor(c.child_id)} size={16} ring={false} /> {kidName(c.child_id)}</Pill>
                  <Pill tone="sunny"><Icon name="coins" width={12} height={12} /> {c.points}</Pill>
                  {c.recurrence !== "once" && <Pill tone="primary"><Icon name="repeat" width={12} height={12} /> {c.recurrence}</Pill>}
                  <Pill tone={c.status === "pending" ? "coral" : c.status === "approved" ? "primary" : "slate"}>{STATUS_LABEL[c.status]}</Pill>
                </div>
                {c.status === "approved" && <p className="mt-1 text-xs font-medium text-ink-muted" data-testid={`approved-coin-history-${c.id}`}>Approved coins stay in history.</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {showEditControls && c.status === "pending" && <button onClick={() => approve(c.id)} data-testid={`approve-${c.id}`} className="w-9 h-9 rounded-full bg-primary text-white grid place-items-center shadow-sm" aria-label="Approve"><Icon name="check" width={18} height={18} /></button>}
                {showEditControls && c.status !== "approved" && <button onClick={() => openEdit(c)} data-testid={`edit-${c.id}`} className="w-9 h-9 rounded-full bg-slate-100 text-ink-soft grid place-items-center" aria-label="Edit"><Icon name="pencil" width={16} height={16} /></button>}
                {showEditControls && c.status !== "approved" && <button onClick={() => { if (demoGuard()) return; setConfirmDel(c); }} data-testid={`delete-${c.id}`} className="w-9 h-9 rounded-full bg-slate-100 text-ink-soft hover:bg-coral/20 hover:text-coral grid place-items-center" aria-label="Delete"><Icon name="trash-2" width={16} height={16} /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit chore" : "New chore"} testid="chore-modal">
        {!editing && (
          <div className="flex gap-2 mb-4 bg-slate-100 rounded-2xl p-1">
            {[["templates", "Templates"], ["custom", "Custom"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2 rounded-xl text-sm font-bold ${tab === k ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`} data-testid={`tab-${k}`}>{l}</button>
            ))}
          </div>
        )}

        {tab === "templates" && !editing ? (
          <div>
            <TemplatePicker
              templates={templates} kind="chore" accent="primary"
              renderItem={(t) => (
                <button onClick={() => pickTemplate(t)} data-testid="template-item"
                  className="group w-full flex items-center gap-2 p-3 rounded-2xl border-2 border-slate-100 hover:border-primary/40 text-left transition-colors">
                  <IconTile name={t.icon} color={colorFor(t.category)} size={38} floating />
                  <div className="min-w-0"><div className="text-sm font-bold text-ink truncate">{t.title}</div><Coins amount={t.points} className="text-xs text-sunny-fg" /></div>
                </button>
              )}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <Input label="Chore name" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Make the bed" data-testid="chore-title" />
            <div>
              <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Points</span>
              <div className="flex items-center gap-2 flex-wrap">
                {[3, 5, 10, 15, 20].map((p) => (
                  <button key={p} onClick={() => setForm({ ...form, points: p })} className={`px-4 py-2 rounded-xl font-bold text-sm ${form.points === p ? "bg-sunny text-sunny-fg" : "bg-slate-100 text-ink-soft"}`} data-testid={`points-${p}`}>{p}</button>
                ))}
                <input type="number" min="0" value={form.points} onChange={(e) => setForm({ ...form, points: parseInt(e.target.value || "0", 10) })} className="w-20 rounded-xl border-2 border-slate-200 px-3 py-2 font-bold focus-ring" data-testid="points-custom" />
              </div>
            </div>
            <div>
              <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Assign to</span>
              <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
                {children.map((k) => (
                  <button key={k.id} onClick={() => setForm({ ...form, child_id: k.id })} data-testid={`assign-${k.id}`}
                    className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-2xl border-2 ${form.child_id === k.id ? "border-primary bg-primary/5" : "border-slate-100"}`}>
                    <Avatar avatar={k.avatar} color={k.color} size={28} ring={false} /><span className="font-bold text-sm text-ink">{k.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Repeat</span>
              <div className="flex gap-2">
                {RECURRENCE.map((r) => (
                  <button key={r.key} onClick={() => setForm({ ...form, recurrence: r.key })} className={`flex-1 py-2 rounded-xl text-sm font-bold ${form.recurrence === r.key ? "bg-ink text-white" : "bg-slate-100 text-ink-soft"}`} data-testid={`recur-${r.key}`}>{r.label}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Icon</span>
              <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto no-scrollbar">
                {CHORE_ICONS.map((ic) => (
                  <button key={ic} onClick={() => setForm({ ...form, icon: ic })} className={`aspect-square rounded-xl grid place-items-center ${form.icon === ic ? "bg-primary text-white" : "bg-slate-100 text-ink-soft"}`} data-testid={`chore-icon-${ic}`}><Icon name={ic} width={18} height={18} /></button>
                ))}
              </div>
            </div>
            <Button onClick={save} size="lg" className="w-full" data-testid="save-chore">{editing ? "Save changes" : "Assign chore"}</Button>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
          onConfirm={async () => { if (await remove(confirmDel.id)) setConfirmDel(null); }}
        title="Delete this chore?"
        message={confirmDel ? `“${confirmDel.title}” will be removed for good.` : ""}
        confirmLabel="Delete chore"
        testid="delete-chore-confirm"
      />
    </div>
  );
}
